import type { BoardCardShortcut } from "./Board.types";
import { BOARD_PLACE_KEY_LABEL } from "./boardSensors";

/** A key the keyboard hint shows: a `Shortcut` hotkey string and what it does. */
export interface BoardHintKey {
    keys: string;
    label: string;
}

const SELECT_HINT_KEYS: readonly BoardHintKey[] = [
    { keys: "x", label: "Select" },
    { keys: "shift+ArrowDown", label: "Extend" },
    { keys: "mod+a", label: "Select all" },
];

const SELECT_INSTRUCTIONS = `Press X to select the card, Shift with an arrow key to extend the selection, or ${BOARD_PLACE_KEY_LABEL}+A to select every card.`;

const SHORTCUT_LIST = new Intl.ListFormat("en", { type: "disjunction" });

interface BoardSelectionCopyArgs {
    /** The board has a selection (`onSelectionChange`). */
    canSelect: boolean;
    shortcuts: readonly BoardCardShortcut[];
}

const getShortcutInstructions = ({
    canSelect,
    shortcuts,
}: BoardSelectionCopyArgs) => {
    const keys = SHORTCUT_LIST.format(
        shortcuts.map(({ key, label }) => `${key.toUpperCase()} for ${label}`),
    );
    return canSelect
        ? `Press ${keys}. On a selected card they act on every selected card.`
        : `Press ${keys}.`;
};

/** What X, Shift+arrows, ⌘A and the card shortcuts add to the keyboard help. */
export const getBoardSelectionCopy = (args: BoardSelectionCopyArgs) => ({
    instructions: [
        ...(args.canSelect ? [SELECT_INSTRUCTIONS] : []),
        ...(args.shortcuts.length > 0 ? [getShortcutInstructions(args)] : []),
    ],
    hintKeys: [
        ...(args.canSelect ? SELECT_HINT_KEYS : []),
        ...args.shortcuts.map(({ key, label }) => ({ keys: key, label })),
    ],
});
